/**
 * One room, as a card (Co-Living redesign, Slice 3).
 *
 * WHAT THIS REPLACES. The room map's tile, which printed "Status / Belegt" and
 * "Miete / Mt. / CHF 1'490" as label-value rows — a two-column table squeezed
 * into a box. This leads with the two things a manager actually scans for, the
 * name and the rent, and lets the rest settle underneath as quiet metadata.
 *
 * THE STATUS IS NOT RECOMPUTED HERE. It arrives already decided by
 * `getRoomOccupancyStatus`, the one occupancy answer in this codebase. This
 * component maps it to a German word and a tone and does nothing else with it —
 * in particular it never reads `room.status`, which is a different and much
 * weaker field (`normalizeRoom` defaults it to "Frei" for any active room).
 *
 * THE STATUS IS ALWAYS WRITTEN OUT. The tone is decoration on top of a word, so
 * nothing here depends on distinguishing green from amber.
 *
 * MISSING METADATA SIMPLY DOES NOT APPEAR. Area, floor and rent each drop out
 * when unrecorded rather than rendering "0 m²", "EG" or "CHF 0", every one of
 * which is a claim the data does not make.
 *
 * WHO LIVES HERE AND WHEN IT FREES UP ARE ANSWERED, NOT COMPUTED. Both come
 * off the room row itself, which `GET /api/admin/rooms` now carries: the name
 * from `current_tenant_display_name` and the availability sentence from
 * `roomPlanningLabel`, which only maps the backend's `planning_state` to
 * German. This component owns neither answer, and that is the point — the
 * moment a card picks its own tenancy out of a list or adds a day to a
 * move-out date, the page has a second availability engine that will one day
 * disagree with `compute_room_planning` about an early move-out.
 *
 * "VERFÜGBARKEIT", NOT "FREI AB". Planning states compose: a room can be free
 * now and reserved from October, or occupied until October and reserved after
 * that. A bare date cannot say either, so the label names the question and
 * the helper's full sentence answers it.
 *
 * THE DEPOSIT IS A THIRD ANSWER, NOT A THIRD DERIVATION (FK1). `deposit` is
 * already bound to this room's CURRENT tenancy and already classified by the
 * backend; the card turns that one state into one word through
 * `depositSecurityState`, the same helper /admin/kautionen prints from. It
 * never asks whether money arrived, what kind of security this is, or how
 * much: three screens re-deriving that from the raw columns is exactly what
 * made an unconfirmed guarantee read "Versichert" on one and "Ausstehend" on
 * another before K4x.2.
 *
 * STATUS ONLY. No amount, no type, no premium, no provider, no receipt date.
 * A card is a scanning surface — "is the deposit settled" is a yes/no a
 * manager can act on, and everything else belongs on the pages that can show
 * it in context.
 *
 * A WORD, NOT A PILL, AND THAT IS A DELIBERATE DEPARTURE FROM /admin/kautionen.
 * `DepositBadge` would have been the obvious reuse, and it is the right
 * component on the deposit pages. It is not the right one HERE, for two
 * reasons measured rather than assumed. First: in light mode `index.css:473`
 * repaints its `positive` tone to a solid `#639922` with white text, which is
 * about 3.4:1 — under AA for text this size. That shortcoming is shared by
 * every screen already using the badge and is theirs to fix; carrying it onto
 * a surface that did not have it would be this slice quietly making the
 * problem bigger. Second: this card already has a place for exactly this kind
 * of fact — the `<dl>` that answers "Mieter" and "Verfügbarkeit" in the
 * card's own text tokens, which follow the theme and are legible in both.
 *
 * THE WORD ITSELF IS STILL NOT OURS. Only the PRESENTATION is page-local:
 * `depositSecurityState` remains the single owner of what a state is called,
 * so /admin/kautionen and this card cannot start naming one state two ways.
 * The tone it returns beside the label is simply not used here — the label is
 * the whole message, which is also why nothing on this card is conveyed by
 * colour alone.
 *
 * THESE THREE ALWAYS APPEAR, EVEN EMPTY — unlike area, floor and rent above. A
 * card that silently omits the tenant reads as "nothing to say here"; "—"
 * says the question was asked and the data has no answer. A room from an
 * older payload with no planning fields at all lands here too, rather than
 * crashing or inventing a date.
 */
import React from "react";
import { Link } from "react-router-dom";
import { BedDouble, Layers, Ruler, Users } from "lucide-react";

import { isActiveRoom, roomPlanningLabel } from "../../../utils/unitOccupancyStatus";
import { depositSecurityState } from "../deposits/depositsLabels";
import { getRoomDetailPath, openRoomLabel } from "./roomConstants";
import {
  formatRoomArea,
  formatRoomFloor,
  formatRoomPrice,
  formatWgSize,
  roomDisplayName,
} from "./roomDisplayHelpers";

/** German word per occupancy status. Unknown stays visibly unknown. */
const STATUS_LABELS = {
  belegt: "Belegt",
  reserviert: "Reserviert",
  frei: "Frei",
  inaktiv: "Inaktiv",
};

/**
 * Badge tones, theme-aware.
 *
 * Same vocabulary as `apartments/UnitListRow.js` so the two redesigned list
 * surfaces read as one product. Tailwind palette steps with a dark counterpart
 * each — no hex, and nothing that stops following the theme.
 *
 * `rounded-md` rather than `rounded-full`: the light-mode `!important` pill
 * overrides in index.css key on a `border-*-200` + `bg-*-50` pair, which these
 * classes do not use, but a square-ish badge also suits a dense card better
 * than a lozenge.
 */
const STATUS_TONES = {
  belegt: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  reserviert: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  frei: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  // The SAME muted pair as `unknown`, and for a related reason: neither state
  // is a claim about lettability. A stood-down room is not free (rose would
  // read as "fill this"), and it is not a problem to solve either -- it is
  // simply out of the rentable population. Reuses the existing semantic
  // tokens; choosing a tone OF ITS OWN is OC4c's question, not this slice's.
  inaktiv: "bg-muted text-muted-foreground",
  unknown: "bg-muted text-muted-foreground",
};

/**
 * One metadata item.
 *
 * `data-meta` is a deliberate test seam. CI runs a Babel plugin that wraps
 * dynamic JSX expressions in extra elements (CLAUDE.md §8.4), which splits
 * "18" and " m²" into different nodes and breaks text lookups that pass
 * locally. A stable attribute is immune to it — the same reasoning as
 * `data-stat` in `apartments/UnitListRow.js`.
 */
function RoomMeta({ icon: Icon, label, value }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground"
      data-meta={label}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {value}
    </span>
  );
}

/**
 * Whatever `depositSecurityState` returns for a state it does not recognise.
 *
 * Read off the helper instead of retyping its em dash: a second copy of that
 * glyph here would be a second place to change, and the point of routing every
 * word through the helper is that there is only one.
 *
 * It exists so that "no tenancy", "a tenancy this page did not load" and "a
 * state this frontend has never heard of" all reach `RoomFact` as the SAME
 * nothing, and are drawn in the same muted way as an unknown tenant — rather
 * than one of them arriving as a foreground-weight em dash that looks like a
 * value somebody entered.
 */
const NO_DEPOSIT_ANSWER = depositSecurityState(undefined, undefined).label;

/**
 * One label/value fact, stacked.
 *
 * STACKED RATHER THAN TWO COLUMNS, decided on the real card width: the grid
 * these sit in reaches four columns at 2xl, which leaves a card roughly 270px
 * of content, and the availability sentence can read "Frei ab 01.11.2026 ·
 * danach reserviert für <Name>". Halving that to a ~130px column would wrap
 * it to five or six lines on the widest screens. Full width costs one row of
 * height and stays readable at 390px too.
 *
 * `<dt>`/`<dd>` rather than two spans, so the label and the value are
 * programmatically associated and a screen reader reads "Mieter: Mara Muster"
 * instead of two unrelated strings.
 *
 * NO TRUNCATION. `break-words` wraps a long name or a long sentence instead
 * of clipping it, so nothing depends on a `title` tooltip to be readable.
 *
 * `min-w-0` IS LOAD-BEARING, twice. A grid or flex item's automatic minimum
 * size is `min-content`, which for an unbreakable 78-character compound —
 * Swiss tenant names and German street names produce them — is the whole
 * word. Without it the item refuses to shrink and the card scrolls sideways,
 * `break-words` notwithstanding: `overflow-wrap: break-word` wraps text
 * inside a box, it does not shrink the box. Measured, not assumed: a card
 * with such a name overflowed at 1440, 1280 and 768 until these were added.
 * Same pairing as the `h4` above.
 *
 * `data-room-fact` is the same test seam as `data-meta` above, for the same
 * reason (CLAUDE.md §8.4).
 */
function RoomFact({ label, value }) {
  const missing = value == null || value === "";
  return (
    <div className="min-w-0" data-room-fact={label}>
      <dt className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className={`m-0 break-words text-[12px] leading-snug ${
          missing ? "text-muted-foreground" : "text-foreground"
        }`}
      >
        {missing ? "—" : value}
      </dd>
    </div>
  );
}

export default function CoLivingRoomCard({
  room,
  status,
  index = 0,
  wgSize = null,
  // `{ state, depositType }` of the tenancy occupying this room today, or
  // null/undefined when there is none — see `roomDepositSecurity` in
  // `AdminRoomsPage`, which owns the binding.
  deposit = null,
}) {
  if (!room) return null;

  const name = roomDisplayName(room, index);
  const price = formatRoomPrice(room.priceMonthly);
  const area = formatRoomArea(room.size_m2);
  const floor = formatRoomFloor(room.floor);
  const wg = formatWgSize(wgSize);

  // Both read straight off the row. `current_tenant_display_name` is the
  // participant-aware name the backend's planning owner selected for THIS
  // room; `current_tenant_name` beside it is the older `Tenancy.tenant_id`
  // column and can name a different person once a tenancy's primary
  // participant has changed, so it is deliberately not a fallback here.
  const tenantName = room.current_tenant_display_name;
  // INACTIVE OUTRANKS THE OCCUPANCY WORD, and only on this card.
  //
  // `getRoomOccupancyStatus` answers belegt | reserviert | frei and has no
  // branch for a room that has been stood down, so a retired room -- which by
  // definition carries no tenancy -- arrives here as "frei", beside rooms that
  // really can be let today. OC2b already took it out of the counters; the card
  // kept calling it free, so the page said "Aktive Zimmer 3" above four cards,
  // two of them reading "Frei".
  //
  // The precedence is deliberately COMPONENT-LOCAL. Widening the shared helper
  // would move the calendar, the room map and the unit detail in one step, and
  // those are separate surfaces with their own tests.
  //
  // The truth is `isActiveRoom`, never `room.status`: the normalizer does write
  // "Inaktiv" into that column, but OC1 removed the stored status from the
  // occupancy presentation on purpose and this is not the place to revive it.
  const active = isActiveRoom(room);
  const cardStatus = active ? status : "inaktiv";

  // A stood-down room has no availability to state. `RoomFact` already draws a
  // null as the em dash it draws for every other thing it cannot answer, so
  // there is no new copy here -- the badge carries the meaning.
  const availability = active ? roomPlanningLabel(room) : null;

  // ONE STATE IN, ONE WORD OUT. `depositSecurityState` owns the whole mapping —
  // including that "held" reads "Erhalten" for cash and "Hinterlegt" otherwise,
  // and that an unrecognised state renders a blank rather than leaking
  // `some_new_state` into the interface as a word nobody wrote.
  const security = depositSecurityState(deposit?.state, deposit?.depositType);
  // NO STATE AT ALL IS NOT A STATE, and neither is a state the helper could not
  // name. Both become `null`, so `RoomFact` draws them exactly as it draws an
  // unknown tenant instead of printing an em dash that reads like an answer.
  const depositLabel =
    deposit?.state && security.label !== NO_DEPOSIT_ANSWER ? security.label : null;

  const statusLabel = STATUS_LABELS[cardStatus] || "Unbekannt";
  const statusTone = STATUS_TONES[cardStatus] || STATUS_TONES.unknown;

  // A room without an id stays a plain box rather than linking to
  // /admin/rooms/undefined, which would look clickable and land on a 404.
  const detailPath = getRoomDetailPath(room.roomId || room.id);
  const Card = detailPath ? Link : "div";
  const cardProps = detailPath
    ? { to: detailPath, "aria-label": openRoomLabel(name) }
    : {};

  return (
    <Card
      {...cardProps}
      data-room-card={name}
      className={`flex flex-col rounded-[12px] border border-border bg-card p-3.5 ${
        detailPath
          ? "transition-colors hover:border-foreground/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          : ""
      }`}
    >
      <div className="flex items-start gap-2">
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] bg-muted text-muted-foreground"
          aria-hidden="true"
        >
          <BedDouble className="h-3.5 w-3.5" />
        </span>

        <h4 className="m-0 min-w-0 flex-1 break-words text-[13px] font-semibold leading-snug text-foreground">
          {name}
        </h4>

        <span
          className={`shrink-0 rounded-md px-2 py-[3px] text-[10.5px] font-semibold ${statusTone}`}
          data-room-status={cardStatus || "unknown"}
        >
          {statusLabel}
        </span>
      </div>

      {price ? (
        <p className="m-0 mt-3 text-[15px] font-semibold tabular-nums text-foreground" data-room-price>
          {price}
        </p>
      ) : null}

      {area || wg || floor ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {area ? <RoomMeta icon={Ruler} label="Fläche" value={area} /> : null}
          {wg ? <RoomMeta icon={Users} label="WG-Größe" value={wg} /> : null}
          {floor ? <RoomMeta icon={Layers} label="Etage" value={floor} /> : null}
        </div>
      ) : null}

      <dl className="m-0 mt-3 grid min-w-0 gap-2 border-t border-border pt-2.5">
        <RoomFact label="Mieter" value={tenantName} />
        {/* Between the two on purpose: "Mieter" and "Kaution" both describe
            the tenancy that is here NOW, while "Verfügbarkeit" is the
            forward-looking one and reads last. */}
        <RoomFact label="Kaution" value={depositLabel} />
        <RoomFact label="Verfügbarkeit" value={availability} />
      </dl>
    </Card>
  );
}
